import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Person } from '../auth/session';
import { useBackend } from '../backend/BackendContext';
import { DEFAULT_LANGUAGE, type Language } from '../domain/profile';
import { readProfile, saveLanguage } from '../store/profile-store';
import { SIGNED_OUT_LANGUAGE, setLanguage } from './index';

interface LanguageValue {
  language: Language;
  /** Saves the choice in the person's profile, then shows the app in it (T076d). */
  changeLanguage: (language: Language) => Promise<void>;
}

const LanguageContext = createContext<LanguageValue | undefined>(undefined);

/**
 * Shows the signed-in person's screens in their language, read from their profile (T076c,
 * FR-035). French until the profile is read; English again after sign-out (owner, 2026-10-13).
 * Changing the language re-renders everything below, so every `t()` picks up the new catalogue.
 */
export function LanguageProvider({ person, children }: { person: Person; children: ReactNode }) {
  const { storage } = useBackend();
  // Set before the first screen renders, so nothing shows in the sign-in pages' language.
  const [language, setCurrent] = useState<Language>(() => {
    setLanguage(DEFAULT_LANGUAGE);
    return DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    let cancelled = false;
    // An unreadable profile (network, too new) keeps French; nothing is written.
    readProfile(storage, person.id)
      .then((profile) => {
        if (!cancelled && profile) apply(profile.language);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      setLanguage(SIGNED_OUT_LANGUAGE);
    };
  }, [storage, person.id]);

  function apply(next: Language) {
    setLanguage(next);
    setCurrent(next);
  }

  async function changeLanguage(next: Language) {
    await saveLanguage(storage, person.id, person.email, next);
    apply(next);
  }

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {/* A new key re-renders every screen below in the new language. */}
      <div key={language} style={{ display: 'contents' }}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageValue {
  const value = useContext(LanguageContext);
  if (!value) {
    throw new Error('useLanguage() must be used within a <LanguageProvider>');
  }
  return value;
}
